import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { hasPermission, makeTerms, type RpcToolsResponse, type Terms } from '@phinest/shared';
import { fetchTools, RpcError } from './rpc';
import { supabase } from './supabase';
import { useAuth } from './auth';

export type Workspace = RpcToolsResponse['workspace'];

interface WorkspaceValue {
  workspace: Workspace;
  /** Tools this user may call; drives which actions the UI offers. */
  tools: Map<string, RpcToolsResponse['tools'][number]>;
  terms: Terms;
  can(permission: string): boolean;
  hasTool(name: string): boolean;
}

const WorkspaceContext = React.createContext<WorkspaceValue | null>(null);

export function useWorkspaceQuery() {
  const { session } = useAuth();
  return useQuery<RpcToolsResponse, RpcError>({
    queryKey: ['workspace', session?.user.id ?? null],
    queryFn: ({ signal }) => fetchTools(signal),
    enabled: !!session,
    staleTime: 60_000,
  });
}

export function WorkspaceProvider({ data, children }: { data: RpcToolsResponse; children: React.ReactNode }) {
  const value = React.useMemo<WorkspaceValue>(() => {
    const tools = new Map(data.tools.map((t) => [t.name, t]));
    const permissions = data.workspace.permissions ?? [];
    return {
      workspace: data.workspace,
      tools,
      terms: makeTerms(data.workspace.terminology),
      can: (p) => hasPermission(permissions, p),
      hasTool: (n) => tools.has(n),
    };
  }, [data]);
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

function ctx(): WorkspaceValue {
  const v = React.useContext(WorkspaceContext);
  if (!v) throw new Error('useWorkspace must be used inside WorkspaceProvider');
  return v;
}
export const useWorkspace = (): Workspace => ctx().workspace;
export const useTerms = (): Terms => ctx().terms;
export const useCan = (): ((permission: string) => boolean) => ctx().can;
export const useHasTool = (): ((name: string) => boolean) => ctx().hasTool;
/** Currency and time zone, needed by nearly every formatter. */
export function useLocaleInfo(): { currency: string; timezone: string } {
  const w = useWorkspace();
  return { currency: w.currency, timezone: w.timezone };
}

/** A tenant setting, e.g. useSetting('tax_label', 'Tax'). */
export function useSetting<T = unknown>(key: string, fallback: T): T {
  const w = useWorkspace();
  const v = (w.settings ?? {})[key];
  return (v === undefined || v === null ? fallback : v) as T;
}

// ---------------------------------------------------------------------------
// Workspace membership and switching
// ---------------------------------------------------------------------------
export interface MembershipRow {
  tenant_id: string;
  role_key: string;
  name: string;
  slug: string;
}

export function useMemberships() {
  const { user } = useAuth();
  return useQuery<MembershipRow[]>({
    queryKey: ['memberships', user?.id ?? null],
    enabled: !!user,
    staleTime: 60_000,
    queryFn: async () => {
      // Row-level security also exposes co-workers in the active workspace, so
      // scope to this user explicitly.
      const { data, error } = await supabase
        .from('memberships')
        .select('tenant_id, role_key, tenants!inner(name, slug)')
        .eq('user_id', user!.id)
        .eq('status', 'active');
      if (error) throw new Error(error.message);
      return (data ?? []).map((r) => {
        const t = r.tenants as unknown as { name: string; slug: string };
        return { tenant_id: r.tenant_id as string, role_key: r.role_key as string, name: t?.name ?? 'Workspace', slug: t?.slug ?? '' };
      });
    },
  });
}

/**
 * Switch workspace: record the choice, mint a token carrying the new tenant,
 * then drop every cached read from the previous workspace.
 */
export function useSelectWorkspace() {
  const { refresh } = useAuth();
  const qc = useQueryClient();
  return React.useCallback(
    async (tenantId: string) => {
      const { error } = await supabase.rpc('select_workspace', { p_tenant_id: tenantId });
      if (error) throw new Error(error.message);
      await refresh();
      await qc.invalidateQueries();
    },
    [refresh, qc],
  );
}
